import React, { useEffect, useRef, useState } from 'react';
import ActivityCardList from './ActivityCardList';
import { useActivityStore } from '../../store/activityStore';
import { Activity } from '../../types/activity';
import NewRatingModal from './modals/NewRatingModal';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useActivityLoadingStore } from '../../store/activityLoadingStore';
import { Star } from 'lucide-react';

interface ActivityListProps {
  activities: Activity[];
  isOwnProfile: boolean;
  initialLoadCount?: number;
  batchSize?: number;
  activeTab?: 'current' | 'past';
  onManageSession: (activity: Activity) => void;
  onRematch: (activity: Activity) => void;
  onAccept: (activity: Activity) => void;
  onDeclineRequest: (activity: Activity, reason?: string) => void;
  /** still needed by ActivityCardList to open the modal */
  onRate: (
    activity: Activity,
    ratings: {
      rating_punctuality: number;
      rating_sportsmanship: number;
      rating_skill_match: number;
      rating_communication: number;
    },
    feedback?: string
  ) => Promise<void>;
}

const ActivityList: React.FC<ActivityListProps> = ({
  activities,
  isOwnProfile,
  initialLoadCount = 20,
  batchSize = 10,
  activeTab = 'current',
  onManageSession,
  onRematch,
  onAccept,
  onDeclineRequest,
  onRate,
}) => {
  const { user } = useAuth();
  const { loadingStates } = useActivityLoadingStore();
  // Grab the action from our store that does the actual Supabase write:
  const { rateActivityRequest } = useActivityStore();

  /* infinite-scroll state */
  const [displayed, setDisplayed] = useState<Activity[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* rating modal state */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalActivity, setModalActivity] = useState<Activity | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loaderRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();

  /* ─ initialize first batch ─ */
  useEffect(() => {
    if (!user) {
      setError('Authentication required');
      return;
    }
    const first = activities.slice(0, initialLoadCount);
    setDisplayed(first);
    setHasMore(first.length < activities.length);
  }, [activities, initialLoadCount, user]);

  /* ─ intersection observer for infinite scroll ─ */
  useEffect(() => {
    if (!user) return;

    const clean = () => {
      observerRef.current?.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    clean();

    observerRef.current = new IntersectionObserver(
      entries => {
        if (!entries[0].isIntersecting || !hasMore) return;

        setLoading(true);
        timerRef.current = setTimeout(() => {
          setDisplayed(prev => {
            const next = activities.slice(prev.length, prev.length + batchSize);
            setHasMore(prev.length + next.length < activities.length);
            return [...prev, ...next];
          });
          setLoading(false);
        }, 300);
      },
      { root: null, rootMargin: '100px', threshold: 0.1 }
    );

    if (loaderRef.current) observerRef.current.observe(loaderRef.current);
    return clean;
  }, [activities, batchSize, hasMore, user]);

  /* ─ helpers ─ */
  const handleAccept = async (a: Activity) => {
    useActivityLoadingStore.getState().setLoadingState(a.id, 'accepting', true);
    try {
      await onAccept(a);
    } finally {
      useActivityLoadingStore.getState().setLoadingState(a.id, 'accepting', false);
    }
  };

  const handleDecline = async (a: Activity, reason?: string) => {
    useActivityLoadingStore.getState().setLoadingState(a.id, 'declining', true);
    try {
      await onDeclineRequest(a, reason);
    } finally {
      useActivityLoadingStore.getState().setLoadingState(a.id, 'declining', false);
    }
  };

  const openModal = (a: Activity) => {
    setModalActivity(a);
    setModalOpen(true);
  };

  const submitRating = async (
    ratings: {
      rating_punctuality: number;
      rating_sportsmanship: number;
      rating_skill_match: number;
      rating_communication: number;
    },
    feedback: string
  ) => {
    if (!modalActivity) return;
    setSubmitting(true);
    try {
      // Map to the shape rateActivityRequest expects:
      await rateActivityRequest(modalActivity.id, {
        punctuality:   ratings.rating_punctuality,
        sportsmanship: ratings.rating_sportsmanship,
        skill_match:   ratings.rating_skill_match,
        communication: ratings.rating_communication
      }, feedback);

      setModalOpen(false);
      setModalActivity(null);
    } catch (err) {
      console.error('Rating submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  /* ─ render ─ */
  if (error)
    return (
      <div className="text-center py-12 bg-dark-300 rounded-xl border border-dark-400">
        <p className="text-dark-800">{error}</p>
      </div>
    );

  if (!activities.length)
    return (
      <div className="text-center py-12 bg-dark-300 rounded-xl border border-dark-400">
        <p className="text-dark-800">No activities found</p>
      </div>
    );

  return (
    <div className="space-y-4 relative">
      {displayed.map(act => {
        const canRate =
          act.status === 'played' &&
          !act.isRated &&
          act.rating_overall === undefined;

        return (
          <ActivityCardList
            key={`list-${act.id}`}
            activities={[act]}
            isOwnProfile={isOwnProfile}
            isPastActivity={activeTab === 'past'}
            onManageSession={onManageSession}
            onRematch={onRematch}
            onAccept={() => handleAccept(act)}
            onDeclineRequest={reason => handleDecline(act, reason)}
           onRate={() => openModal(act)}
            isAccepting={loadingStates[act.id]?.isAccepting}
            isDeclining={loadingStates[act.id]?.isDeclining}
            isRating={loadingStates[act.id]?.isRating}
          />
        );
      })}

      {hasMore ? (
        <div
          ref={loaderRef}
          className={cn(
            'flex justify-center py-4 transition-opacity duration-300',
            isLoading ? 'opacity-100' : 'opacity-0'
          )}
        >
          <Loader2 className="h-6 w-6 text-gold-400 animate-spin" />
        </div>
      ) : (
        <div className="text-center text-dark-800">No more activities to load</div>
      )}

      {modalActivity && (
        <NewRatingModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={submitRating}
          playerName={modalActivity.opponent.name}
          venueName={modalActivity.venue.name}
          isSubmitting={submitting}
        />
      )}
    </div>
  );
};

export default ActivityList;
